import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import type * as THREE from 'three';
import type { ModelLoaderType } from './modelFiles';
import { useViewerEnvironment } from './environment';
import type { ReportError } from './environment';
import type { SharedCamera } from './sharedCamera';
import type { ViewPreset } from './cameraRig';
import { DEFAULT_DISPLAY_MODE } from './ModelViewer.constants';
import { DecoderUnavailable, parseModel } from './modelLoader';
import {
  BACKDROP_MAX_BYTES,
  BACKDROP_TYPES,
  DEFAULT_SECTION,
  DEFAULT_UNDER_LIGHT,
  IDLE_MEASURE,
  cubeLayout,
  cssColor,
  decodeBackdrop,
  measureColor,
  resizeSection,
} from './viewport';
import type { DimensionAxis, MeasureState, SectionAxis, SectionSettings, SelectedPart, UnderLightSettings } from './viewport';
import { ViewerStage } from './stage/ViewerStage';
import type { DisplayMode } from './stage/displayModes';
import { DEFAULT_ENVIRONMENT } from './viewerSettings';
import type { Environment, EnvironmentToggle, SelectionMode } from './viewerSettings';
import ViewportPanels from './ViewportPanels';
import type { ModelSize } from './format';
import AiRenderResult from './chrome/ai/AiRenderResult';
import { ChromeContext } from './chrome/ChromeContext';
import { GRID_READOUT_MIN_PX, MINI_HEIGHT_PX, MINI_WIDTH_PX, NARROW_PX } from './chrome/chrome.constants';
import { DISPLAY_MODES } from './chrome/displayModes';
import ExpandButton from './chrome/ExpandButton';
import { messages } from './chrome/messages';
import { DimensionLabels, MeasureLabel } from './chrome/SceneLabels';
import { useCoarsePointer } from './chrome/useCoarsePointer';
import { useViewerShortcuts } from './chrome/useViewerShortcuts';
import { useViewportSize, type ViewportSize } from './chrome/useViewportSize';
import ViewerChrome from './chrome/ViewerChrome';
import ViewerStatus from './chrome/ViewerStatus';
import type { ViewerStatusKind } from './chrome/ViewerStatus';
import { ModelViewerWrapper, ModelViewerCanvas } from './ModelViewer.styled';

export type { CameraPose } from './cameraRig';
export type { DisplayMode } from './stage/displayModes';

/**
 * Which step of the load pipeline failed. Reported alongside the error so a
 * decode failure (bad bytes) can be told apart from a scene-setup failure
 * (WebGL / three.js) without reading stack traces.
 */
type LoadFailureStage = 'read' | 'parse' | 'render';

type MeasureUnavailable = 'noModel' | 'tooDense';

function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

function reportLoadFailure(
  onError: ReportError | undefined,
  err: unknown,
  stage: LoadFailureStage,
  fileName?: string,
  loaderType?: ModelLoaderType,
): void {
  // Not console.error: RUM records that as a second error for the same failure.
  console.warn(`[ModelViewer] Failed to load ${fileName ?? 'empty workbench'}:`, err);
  onError?.(toError(err), { fileName, loaderType, stage });
}

// Bytes that already failed to decode. A card remounts its viewer on every
// scroll back into view, and the same bytes would fail and be reported again.
const undecodable = new WeakSet<Blob>();

/** What the viewer lets its owner do to the scene from outside. */
export interface ModelViewerHandle {
  /** A PNG of the frame on screen, or null while there is no model in it. */
  captureFrame: () => string | null;
}

/** A model to draw, or nothing: an empty workbench is grid and camera alone. */
type ModelSource =
  | {
      /**
       * The model bytes. Deliberately a {@link Blob} rather than an object URL:
       * an object URL can be revoked by its owner while this component is still
       * loading from it, which fails the read with `TypeError: Failed to fetch`
       * (AIB-7393). A Blob reference stays valid for as long as we hold it.
       */
      data: Blob;
      fileName: string;
      loaderType: ModelLoaderType;
    }
  | { data?: null; fileName?: undefined; loaderType?: undefined };

type ModelViewerProps = ModelSource & {
  /** A thumbnail: the part alone, without bars, tools or keys. */
  compact?: boolean;
  displayMode?: DisplayMode;
  /** Told when the shading menu or a key changes the mode, e.g. to open the full-screen view in it. */
  onDisplayModeChange?: (mode: DisplayMode) => void;
  /** Stops the render loop: a hidden viewer is still a WebGL context at 60fps. */
  paused?: boolean;
  /**
   * Shared with the other viewers of one part, which turn with this one, and
   * with the models that replace it, which open on its last pose.
   */
  cameraPose?: SharedCamera;
  /** Shows an expand button that asks the owner for the full-screen view. */
  onExpand?: () => void;
};

interface Latest {
  displayMode: DisplayMode;
  paused: boolean;
  measuring: boolean;
  section: SectionSettings | null;
  environment: Environment;
  underLight: UnderLightSettings;
  fixedSpacing: number | null;
  dimensions: boolean;
  selectionMode: SelectionMode;
  backdrop: ImageBitmap | null;
}

const BACKGROUND_NOTICE_MS = 4000;

interface StatusInputs {
  failed: boolean;
  hasModel: boolean;
  modelShown: boolean;
  compact: boolean;
}

function statusOf({ failed, hasModel, modelShown, compact }: StatusInputs): ViewerStatusKind | null {
  if (failed) return 'failed';
  if (hasModel && !modelShown) return 'loading';
  if (!hasModel && !compact) return 'empty';
  return null;
}

function layoutOf({ width, height }: ViewportSize, compact: boolean) {
  const measured = width > 0;
  const mini = !compact && measured && (width < MINI_WIDTH_PX || height < MINI_HEIGHT_PX);
  return { mini, tools: !compact && !mini, narrow: measured && width < NARROW_PX };
}

const HIDDEN_TOOLS = { section: null, measuring: false, dimensions: false } as const;

const ModelViewer = forwardRef<ModelViewerHandle, ModelViewerProps>(function ModelViewer(
  {
    data,
    fileName,
    loaderType,
    compact = false,
    displayMode: displayModeProp = DEFAULT_DISPLAY_MODE,
    onDisplayModeChange,
    paused = false,
    cameraPose,
    onExpand,
  },
  ref,
) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const { onError, viewRender } = useViewerEnvironment();
  const theme = useTheme() as { isDarkTheme?: boolean };
  const isDark = theme.isDarkTheme === true;
  const stageRef = useRef<ViewerStage | null>(null);
  const openPopoversRef = useRef(0);
  const size = useViewportSize(wrapperRef);
  const coarse = useCoarsePointer();
  const [displayMode, setDisplayMode] = useState<DisplayMode>(displayModeProp);
  const [givenDisplayMode, setGivenDisplayMode] = useState(displayModeProp);
  if (displayModeProp !== givenDisplayMode) {
    setGivenDisplayMode(displayModeProp);
    setDisplayMode(displayModeProp);
  }
  const [contextLost, setContextLost] = useState(false);
  const [initFailed, setInitFailed] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [shownData, setShownData] = useState<Blob | null>(null);
  const [section, setSection] = useState<SectionSettings | null>(null);
  const [sectionOffset, setSectionOffset] = useState(0);
  const [measuring, setMeasuring] = useState(false);
  const [measure, setMeasure] = useState<MeasureState>(IDLE_MEASURE);
  const [selectedPart, setSelectedPart] = useState<SelectedPart | null>(null);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('body');
  const [dimensions, setDimensions] = useState(false);
  const [environment, setEnvironment] = useState<Environment>(DEFAULT_ENVIRONMENT);
  const [underLight, setUnderLight] = useState<UnderLightSettings>(DEFAULT_UNDER_LIGHT);
  const [fixedSpacing, setFixedSpacing] = useState<number | null>(null);
  const [backdrop, setBackdrop] = useState<ImageBitmap | null>(null);
  const [backdropError, setBackdropError] = useState<string | undefined>();
  const [cubeHovered, setCubeHovered] = useState(false);
  const [renderResult, setRenderResult] = useState<string | null>(null);
  const [measured, setMeasured] = useState<ModelSize | null>(null);
  const [gridSpacing, setGridSpacing] = useState<number | null>(null);
  const [measureUnavailable, setMeasureUnavailable] = useState<MeasureUnavailable | undefined>('noModel');
  const hasModel = Boolean(data && loaderType);
  const modelSize = hasModel ? measured : null;
  const [hadModel, setHadModel] = useState(hasModel);
  if (hasModel !== hadModel) {
    setHadModel(hasModel);
    if (!hasModel) setMeasuring(false);
  }
  const { mini, tools, narrow } = layoutOf(size, compact);
  // A viewer shrunk below its tools stops showing them, and shows them again once it grows.
  const scene = mini ? HIDDEN_TOOLS : { section, measuring, dimensions };
  const latest = useRef<Latest>({
    displayMode,
    paused,
    ...scene,
    environment,
    underLight,
    fixedSpacing,
    selectionMode,
    backdrop,
  });
  useLayoutEffect(() => {
    latest.current = {
      displayMode,
      paused,
      ...scene,
      environment,
      underLight,
      fixedSpacing,
      selectionMode,
      backdrop,
    };
  });
  const showingResult = renderResult !== null;
  const failed = contextLost || initFailed || loadError;

  useImperativeHandle(
    ref,
    () => ({ captureFrame: () => stageRef.current?.capture() ?? null }),
    [],
  );

  const changeDisplayMode = useCallback(
    (mode: DisplayMode) => {
      setDisplayMode(mode);
      onDisplayModeChange?.(mode);
    },
    [onDisplayModeChange],
  );

  const handleRetry = useCallback(() => {
    if (data) undecodable.delete(data);
    setContextLost(false);
    setInitFailed(false);
    setLoadError(false);
    setRetryKey((k) => k + 1);
  }, [data]);

  useEffect(() => {
    stageRef.current?.setPaused(paused || showingResult);
  }, [paused, showingResult]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const settings = latest.current;
    const stage = ViewerStage.create({
      container,
      isDark,
      compact,
      cameraPose,
      displayMode: settings.displayMode,
      paused: settings.paused,
      measuring: settings.measuring,
      section: settings.section,
      environment: settings.environment,
      underLight: settings.underLight,
      gridSpacing: settings.fixedSpacing,
      dimensions: settings.dimensions,
      cubeLabels: {
        front: intl.formatMessage(messages.viewFront),
        back: intl.formatMessage(messages.viewBack),
        right: intl.formatMessage(messages.viewRight),
        left: intl.formatMessage(messages.viewLeft),
        top: intl.formatMessage(messages.viewTop),
        bottom: intl.formatMessage(messages.viewBottom),
      },
      events: {
        onContextLost: setContextLost,
        onGridSpacing: setGridSpacing,
        onSelectedPart: setSelectedPart,
        onMeasure: setMeasure,
        onCastable: (castable) => {
          if (castable) {
            setMeasureUnavailable(undefined);
          } else {
            setMeasureUnavailable('tooDense');
            setMeasuring(false);
          }
        },
        onModelSize: setMeasured,
        onCubeHover: setCubeHovered,
        onSectionChange: setSection,
        onSectionOffset: setSectionOffset,
      },
    });
    if (!stage) {
      queueMicrotask(() => setInitFailed(true));
      return;
    }
    stageRef.current = stage;
    stage.setSelectionMode(settings.selectionMode);
    stage.setBackdrop(settings.backdrop);

    if (!hasModel) {
      try {
        stage.showWorkbench();
      } catch (err) {
        reportLoadFailure(onError, err, 'render');
        setLoadError(true);
      }
    }

    return () => {
      stageRef.current = null;
      setSelectedPart(null);
      setMeasured(null);
      setMeasureUnavailable('noModel');
      setMeasure(IDLE_MEASURE);
      stage.dispose();
    };
  }, [isDark, compact, retryKey, cameraPose, hasModel, onError, intl]);

  // Also keyed on the viewer's deps: a rebuilt scene starts empty.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !data || !loaderType) return;
    let stale = false;

    // Never reported once a newer model, a rebuild or an unmount overtook it:
    // that is routine, not an error worth putting in front of a user or in RUM.
    const fail = (err: unknown, step: LoadFailureStage, bytesAtFault = step !== 'render') => {
      if (stale) return;
      if (bytesAtFault) undecodable.add(data);
      reportLoadFailure(onError, err, step, fileName, loaderType);
      setLoadError(true);
    };

    const show = (object: THREE.Object3D) => {
      if (stale) {
        stage.discard(object);
        return;
      }
      try {
        stage.show(object);
        setLoadError(false);
        setShownData(data);
      } catch (err) {
        fail(err, 'render');
      }
    };

    const load = async () => {
      let buffer: ArrayBuffer;
      try {
        buffer = await data.arrayBuffer();
      } catch (err) {
        fail(err, 'read');
        return;
      }
      if (stale) return;
      let object: THREE.Object3D;
      try {
        object = await parseModel(buffer, loaderType, stage.materials);
      } catch (err) {
        if (err instanceof DecoderUnavailable) fail(err.reason, 'parse', false);
        else fail(err, 'parse');
        return;
      }
      show(object);
    };

    if (undecodable.has(data)) {
      queueMicrotask(() => {
        if (!stale) setLoadError(true);
      });
    } else {
      void load();
    }

    return () => {
      stale = true;
    };
  }, [data, fileName, loaderType, isDark, compact, retryKey, cameraPose, onError, intl]);

  useEffect(() => {
    stageRef.current?.setDisplayMode(displayMode);
  }, [displayMode, isDark]);

  useEffect(() => {
    stageRef.current?.setSection(scene.section);
  }, [scene.section]);

  useEffect(() => {
    stageRef.current?.setMeasuring(scene.measuring);
  }, [scene.measuring]);

  useEffect(() => {
    stageRef.current?.setSelectionMode(selectionMode);
  }, [selectionMode]);

  useEffect(() => {
    stageRef.current?.setEnvironment(environment);
  }, [environment]);

  useEffect(() => {
    stageRef.current?.setUnderLight(environment.underLight ? underLight : null);
  }, [environment.underLight, underLight]);

  useEffect(() => {
    stageRef.current?.setGridSpacing(fixedSpacing);
  }, [fixedSpacing]);

  useEffect(() => {
    stageRef.current?.setDimensions(scene.dimensions);
  }, [scene.dimensions]);

  useEffect(() => {
    stageRef.current?.setBackdrop(backdrop);
    return () => backdrop?.close();
  }, [backdrop]);

  useEffect(() => {
    if (!backdropError) return undefined;
    const timer = setTimeout(() => setBackdropError(undefined), BACKGROUND_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [backdropError]);

  const clearSelection = useCallback(() => stageRef.current?.clearSelection(), []);
  const stopMeasuring = useCallback(() => setMeasuring(false), []);
  const attachMeasureLabel = useCallback(
    (label: HTMLDivElement | null) => stageRef.current?.setMeasureLabel(label),
    [],
  );
  const attachDimensionLabel = useCallback(
    (axis: DimensionAxis, element: HTMLDivElement | null) => stageRef.current?.attachDimensionLabel(axis, element),
    [],
  );
  const attachSectionBar = useCallback(
    (element: HTMLDivElement | null) => stageRef.current?.attachSectionBar(element),
    [],
  );
  const home = useCallback(() => stageRef.current?.home(), []);
  const preset = useCallback((next: ViewPreset) => stageRef.current?.setViewPreset(next), []);
  const captureView = useCallback((maxEdge: number) => stageRef.current?.captureView(maxEdge) ?? null, []);
  const toggleMeasure = useCallback(() => {
    if (measureUnavailable === undefined) setMeasuring((on) => !on);
  }, [measureUnavailable]);
  const toggleSection = useCallback(() => setSection((current) => (current ? null : DEFAULT_SECTION)), []);
  const toggleEnvironment = useCallback(
    (key: EnvironmentToggle) => setEnvironment((current) => ({ ...current, [key]: !current[key] })),
    [],
  );
  const changeSectionOffset = useCallback((offset: number) => {
    setSection((current) => {
      const stage = stageRef.current;
      if (!current || !stage) return current;
      return { ...current, position: stage.sectionPositionFor(current.axis as SectionAxis, offset) };
    });
  }, []);

  const loadBackdrop = useCallback(
    async (file: File) => {
      if (!(BACKDROP_TYPES as readonly string[]).includes(file.type) || file.size > BACKDROP_MAX_BYTES) {
        setBackdropError(intl.formatMessage(messages.backgroundUnsupported));
        return;
      }
      try {
        setBackdrop(await decodeBackdrop(file));
        setBackdropError(undefined);
      } catch {
        setBackdropError(intl.formatMessage(messages.backgroundUnreadable));
      }
    },
    [intl],
  );

  useViewerShortcuts(
    wrapperRef,
    {
      onShading: (index) => changeDisplayMode(DISPLAY_MODES[index].mode),
      onZoom: () => stageRef.current?.zoomToPointer(),
      onFit: home,
      onProjection: () => stageRef.current?.toggleProjection(),
      onMeasure: toggleMeasure,
      onSectionSize: (grow) => setSection((current) => (current ? resizeSection(current, grow) : current)),
      onEscape: () => {
        if (showingResult) {
          setRenderResult(null);
          return true;
        }
        if (measuring) {
          setMeasuring(false);
          return true;
        }
        if (stageRef.current?.clearSelection()) return true;
        if (section) {
          setSection(null);
          return true;
        }
        return false;
      },
    },
    tools && hasModel && shownData === data,
    openPopoversRef,
  );

  const modelShown = hasModel && shownData === data;
  const pickUnavailable =
    measureUnavailable === 'tooDense' ? intl.formatMessage(messages.measureTooDense) : undefined;

  const status = statusOf({ failed, hasModel, modelShown, compact });

  return (
    <ChromeContext.Provider value={{ narrow, coarse, openPopoversRef }}>
      <ModelViewerWrapper ref={wrapperRef}>
        <ModelViewerCanvas ref={containerRef} $crosshair={scene.measuring} />
        {mini && !failed && onExpand && <ExpandButton onExpand={onExpand} standalone />}
        {tools && !failed && (
          <ViewerChrome
            isDark={isDark}
            hasModel={modelShown}
            narrow={narrow}
            showGridReadout={size.width >= GRID_READOUT_MIN_PX}
            pickUnavailable={pickUnavailable}
            displayMode={displayMode}
            onDisplayModeChange={changeDisplayMode}
            selectionMode={selectionMode}
            onSelectionModeChange={setSelectionMode}
            section={modelShown ? section : null}
            sectionOffset={sectionOffset}
            onToggleSection={toggleSection}
            onSectionChange={setSection}
            onSectionOffsetChange={changeSectionOffset}
            attachSectionBar={attachSectionBar}
            measuring={measuring}
            onToggleMeasure={toggleMeasure}
            dimensions={dimensions}
            onToggleDimensions={() => setDimensions((on) => !on)}
            environment={environment}
            onToggleEnvironment={toggleEnvironment}
            underLight={underLight}
            onUnderLightChange={setUnderLight}
            backgroundOn={backdrop !== null}
            backgroundError={backdropError}
            onBackgroundFile={(file) => void loadBackdrop(file)}
            onBackgroundRemove={() => setBackdrop(null)}
            gridSpacing={gridSpacing}
            fixedGridSpacing={fixedSpacing}
            onGridSpacingChange={setFixedSpacing}
            cubeLayout={cubeLayout(size.width, size.height)}
            cubeHovered={cubeHovered}
            onResetView={home}
            onPreset={preset}
            onFitView={home}
            onExpand={onExpand}
            viewRender={viewRender}
            captureView={captureView}
            onRenderResult={setRenderResult}
          />
        )}
        {tools && (
          <ViewportPanels
            part={selectionMode === 'body' ? selectedPart : null}
            measuring={measuring}
            measure={measure}
            modelSize={measureUnavailable === 'tooDense' ? modelSize : null}
            onClosePart={clearSelection}
            onCloseMeasure={stopMeasuring}
          />
        )}
        {tools && dimensions && modelSize && (
          <DimensionLabels size={modelSize} isDark={isDark} attach={attachDimensionLabel} />
        )}
        {measure.measurement && (
          <MeasureLabel
            distance={measure.measurement.distance}
            tint={cssColor(measureColor(isDark))}
            attach={attachMeasureLabel}
          />
        )}
        {renderResult && (
          <AiRenderResult image={renderResult} fileName={fileName} onBack={() => setRenderResult(null)} />
        )}
        {status && <ViewerStatus status={status} onRetry={handleRetry} />}
      </ModelViewerWrapper>
    </ChromeContext.Provider>
  );
});

export default ModelViewer;
